import { StageDef } from '../automationMachineInterfaces'
import { REDDIT_AUTOMATION_STAGE_STATUSES } from './redditAutomationStageStatuses'
import * as redditUrls from './redditUrls'
import { RedditSettingKey } from '../../recommendations/recommendationSettingsKeys'
import * as enums from '../../utils/enums'
import {
  getWaitForElementToAppearPromise,
  wait,
} from '../interruptiblePromises'
import { evaluateXpath } from '../interruptibleXpath'
import {
  defaultCantUndoFn,
  defaultIsDisabledFn,
  defaultIsDisabledReasonFn,
  getCurrentValueFnFromKey,
  getIsFulfilledFnFromKey,
} from '../recommendationCardDisplayUtils'

type ChatSetting = {
  dialogId: string
  dialogDescribedBy: string
  dropdownId: string
  newTarget: string
  oldTarget: string
  rowId: string
  section: string
  key: RedditSettingKey
}

const settings: ChatSetting[] = [
  {
    dialogId: 'allow-chat-requests',
    dialogDescribedBy: 'allow-chat-requests-desc',
    dropdownId: 'SettingDropdown--chatUserSettingsInvitePolicy',
    newTarget: 'Accounts older than 30 days',
    oldTarget: 'Accounts older than 30 days',
    rowId: 'allow-chat-requests',
    section: 'settings-privacy-section',
    key: RedditSettingKey.CHAT_REQUESTS,
  },
]

const getCurrentValueFn = (key: RedditSettingKey) =>
  getCurrentValueFnFromKey(key, (value: string) => {
    switch (key) {
      case RedditSettingKey.CHAT_REQUESTS:
        return value
      default:
        throw new Error(`Unknown key: ${key}`)
    }
  })

function getCorrectValues(key: RedditSettingKey): string[] {
  switch (key) {
    case RedditSettingKey.CHAT_REQUESTS:
      return ['Accounts older than 30 days', 'Nobody']
    default:
      throw new Error(`Unknown key: ${key}`)
  }
}

const getIsFulfilledFn = (key: RedditSettingKey) =>
  getIsFulfilledFnFromKey(key, (value) => {
    const correctValues = getCorrectValues(key)
    return correctValues.includes(value)
  })

async function getTriggerSpanEl(id: string): Promise<HTMLSpanElement> {
  const triggerSpanEl = await getWaitForElementToAppearPromise<HTMLElement>(
    document,
    (node) => evaluateXpath<HTMLElement>(node, `//span[@id="${id}"]`)[0],
    5000,
  )

  if (!triggerSpanEl) {
    throw new Error(
      `redditChat getTriggerSpanEl: Couldn't find span with ID ${id}`,
    )
  }
  return triggerSpanEl
}

async function getDropdownValue(id: string): Promise<string> {
  const triggerSpanEl = await getTriggerSpanEl(id)
  return triggerSpanEl?.textContent?.trim()
}

async function setDropdown(id: string, value: string): Promise<void> {
  const triggerSpanEl = await getTriggerSpanEl(id)
  triggerSpanEl.click()
  const menuitemSpanEl =
    await getWaitForElementToAppearPromise<HTMLSpanElement>(
      document,
      (node) =>
        evaluateXpath<HTMLSpanElement>(
          node,
          `//button[@role="menuitem"]/span[text()="${value}"]`,
        )[0],
      5000,
    )
  if (!menuitemSpanEl) {
    throw new Error(`setDropdown: Couldn't find menu item for ID ${id}`)
  }
  menuitemSpanEl.click()
}

async function openSetting(section: string, id: string): Promise<void> {
  const settingRow = await getWaitForElementToAppearPromise<HTMLElement>(
    document,
    () => {
      const sectionElement = document.querySelector(section)
      if (sectionElement) {
        return sectionElement.shadowRoot.querySelector(
          `div[data-testid="${id}"] span`,
        )
      }
      return null
    },
    5000,
  )

  if (!settingRow) {
    throw new Error(
      `redditChat openSetting: Couldn't find setting row for id ${id}`,
    )
  }

  settingRow.click()
}

async function closeSettingDialog(dialogId: string): Promise<void> {
  const closeDialogButton = await getWaitForElementToAppearPromise<HTMLElement>(
    document,
    (node) =>
      evaluateXpath<HTMLElement>(
        node,
        `//div[@id="${dialogId}"]/descendant::button[@aria-label="Close dialog"]`,
      )[0],
    5000,
  )

  if (!closeDialogButton) {
    throw new Error(
      `redditChat closeSettingDialog: Couldn't find close dialog ${dialogId}`,
    )
  }

  closeDialogButton.click()
}

async function getSettingValue(
  section: string,
  id: string,
  dialogId: string,
): Promise<string> {
  await openSetting(section, id)

  const selectedRow = await getWaitForElementToAppearPromise<HTMLElement>(
    document,
    (node) =>
      evaluateXpath<HTMLElement>(
        node,
        `//div[@id="${dialogId}"]/descendant::div[@aria-selected="true"]`,
      )[0],
    5000,
  )

  await closeSettingDialog(dialogId)

  return selectedRow?.textContent?.trim()
}

async function setSettingValue(
  section: string,
  id: string,
  dialogId: string,
  value: string,
): Promise<void> {
  await openSetting(section, id)

  const targetRow = await getWaitForElementToAppearPromise<HTMLElement>(
    document,
    (node) =>
      evaluateXpath<HTMLElement>(
        node,
        `//div[@id="${dialogId}"]/descendant::span[contains(text(),"${value}")]`,
      )[0],
    5000,
  )

  if (!targetRow) {
    throw new Error(
      `redditChat setSettingValue: Couldn't find target setting row ${value}`,
    )
  }

  targetRow.click()

  await wait(500)
  await closeSettingDialog(dialogId)
}

const HOME_URL_PATTERN = /https:\/\/www\.reddit\.com\/?(\?.*|$)/

const gatherStageDefs: StageDef[] = [
  {
    type: enums.StageType.ACTION,
    key: 'INIT_GATHER_REDDIT_CHAT',
    status: REDDIT_AUTOMATION_STAGE_STATUSES.CHANGING_PAGES,
    url: redditUrls.homeUrl,
    urlPattern: HOME_URL_PATTERN,
    action: async () => {},
  },
  ...settings.map((s) => ({
    type: enums.StageType.ACTION,
    key: `GATHER_REDDIT_SETTING_${s.key}`,
    status: REDDIT_AUTOMATION_STAGE_STATUSES.GATHERING_SETTINGS,
    url: redditUrls.messagingSettingsUrl,
    urlPattern: 'https://www.reddit.com/settings/(messaging|privacy)',
    action: async (machineContext, userContext) => {
      const currentValue = await Promise.any([
        getSettingValue(s.section, s.rowId, s.dialogId),
        getDropdownValue(s.dropdownId),
      ])

      return userContext.updateStoredInitialValues(
        { [s.key]: currentValue },
        enums.RedditRecommendationKey.REDDIT_CHAT,
      )
    },
  })),
]

const getCantUndoFn = () => defaultCantUndoFn

const getIsDisabledFn = () => defaultIsDisabledFn

const getIsDisabledReasonFn = () => defaultIsDisabledReasonFn

const updateStageDefs: StageDef[] = [
  {
    type: enums.StageType.ACTION,
    key: 'INIT_UPDATE_REDDIT_CHAT',
    status: REDDIT_AUTOMATION_STAGE_STATUSES.CHANGING_PAGES,
    url: redditUrls.homeUrl,
    urlPattern: HOME_URL_PATTERN,
    action: async () => {},
  },
  ...settings.map((s) => ({
    type: enums.StageType.ACTION,
    key: `UPDATE_SETTING_${s.key}`,
    status: REDDIT_AUTOMATION_STAGE_STATUSES.UPDATING_SETTINGS,
    url: redditUrls.messagingSettingsUrl,
    urlPattern: 'https://www.reddit.com/settings/(messaging|privacy)',
    action: async (machineContext, userContext) => {
      if (
        !userContext.settingsSelected ||
        !userContext.settingsSelected[s.key]
      ) {
        return
      }
      const currentValue = await Promise.any([
        getSettingValue(s.section, s.rowId, s.dialogId),
        getDropdownValue(s.dropdownId),
      ])

      const correctValues = getCorrectValues(s.key)

      if (!correctValues.includes(currentValue)) {
        try {
          await setDropdown(s.dropdownId, s.oldTarget)
        } catch (_error) {
          await setSettingValue(s.section, s.rowId, s.dialogId, s.newTarget)
        }
      }
    },
  })),
]

const undoStageDefs: StageDef[] = [
  {
    type: enums.StageType.ACTION,
    key: 'INIT_UNDO_REDDIT_CHAT',
    status: REDDIT_AUTOMATION_STAGE_STATUSES.CHANGING_PAGES,
    url: redditUrls.homeUrl,
    urlPattern: HOME_URL_PATTERN,
    action: async () => {},
  },
  ...settings.map((s) => ({
    type: enums.StageType.ACTION,
    key: `UNDO_SETTING_${s.key}`,
    status: REDDIT_AUTOMATION_STAGE_STATUSES.UNDOING_SETTINGS,
    url: redditUrls.messagingSettingsUrl,
    urlPattern: 'https://www.reddit.com/settings/(messaging|privacy)',
    action: async (machineContext, userContext) => {
      if (!userContext.initialValues || !userContext.initialValues[s.key]) {
        return
      }
      const currentValue = await Promise.any([
        getSettingValue(s.section, s.rowId, s.dialogId),
        getDropdownValue(s.dropdownId),
      ])

      if (
        !userContext.isDry &&
        currentValue !== userContext.initialValues[s.key]
      ) {
        try {
          await setDropdown(s.dropdownId, userContext.initialValues[s.key])
        } catch (_error) {
          await setSettingValue(
            s.section,
            s.rowId,
            s.dialogId,
            userContext.initialValues[s.key],
          )
        }
      }
    },
  })),
]

export {
  getCantUndoFn,
  getCurrentValueFn,
  getIsDisabledFn,
  getIsDisabledReasonFn,
  getIsFulfilledFn,
  gatherStageDefs,
  updateStageDefs,
  undoStageDefs,
}
